import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, SegmentedControl, TextSkeleton } from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { SiteVisitorClaimsButton } from '../AdaptiveContent';
import type { SitePreviewContent } from './getSiteURLForContent';
import { SitePreviewButton } from './SitePreviewButton';
import { sitePreviewAtom } from './state';

interface SitePreviewToolbarProps {
    organizationId: string;
    site: api.Site;
    content: SitePreviewContent;
}

/**
 * Toolbar to control the display of the site preview.
 */
export function SitePreviewToolbar(props: SitePreviewToolbarProps) {
    const { organizationId, site } = props;

    const [state, setState] = useRecoilState(sitePreviewAtom);

    return (
        <div className="flex items-center border-b px-2 py-1">
            <div className="flex flex-1 items-center gap-2">
                <PublishOrPreviewButton {...props} />
                {site.adaptiveContent?.enabled ? (
                    <SiteVisitorClaimsButton
                        size="medium"
                        organizationId={organizationId}
                        siteId={site.id}
                    />
                ) : null}
            </div>
            <div className="flex items-center gap-2">
                <SegmentedControl
                    size="small"
                    active={state.theme}
                    onChange={(theme) => setState((prev) => ({ ...prev, theme }))}
                    options={[
                        {
                            value: api.CustomizationThemeMode.Light,
                            label: 'Light',
                            icon: Icon.ThemeLight,
                        },
                        {
                            value: api.CustomizationThemeMode.Dark,
                            label: 'Dark',
                            icon: Icon.ThemeDark,
                        },
                    ]}
                />
                <SegmentedControl
                    size="small"
                    active={state.device}
                    onChange={(device) => setState((prev) => ({ ...prev, device }))}
                    options={[
                        {
                            value: 'desktop',
                            label: 'Desktop',
                            icon: Icon.DeviceDesktop,
                        },
                        {
                            value: 'mobile',
                            label: 'Mobile',
                            icon: Icon.DeviceMobile,
                        },
                    ]}
                />
            </div>
        </div>
    );
}

/**
 * Component to show the URL that can be opened in another tab.
 */
function PublishOrPreviewButton(props: SitePreviewToolbarProps) {
    const { site, organizationId } = props;

    if (!site.published) {
        return (
            <Button
                kind="secondary"
                leadingIcon={Icon.Publish}
                href={{
                    screen: {
                        type: 'site',
                        organization: organizationId,
                        site: site.id,
                        tab: undefined,
                    },
                }}
                label="Publish site"
            />
        );
    }

    return (
        <React.Suspense fallback={<TextSkeleton>Loading URL...</TextSkeleton>}>
            <PreviewButton {...props} />
        </React.Suspense>
    );
}

function PreviewButton(props: SitePreviewToolbarProps) {
    const { site, organizationId, content } = props;
    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId,
        })
    );
    return (
        <SitePreviewButton
            siteId={site.id}
            content={content}
            orgAllContent={orgAllContent}
            kind="blank"
            size="small"
            title="Visit site"
            className="shrink-[5] truncate"
        />
    );
}
